import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import clsx from 'clsx'
import toast from 'react-hot-toast'
import type { VerticalInfo } from '@lipaflow/shared'
import { Badge, Button, EmptyState, Modal, PageHeader } from '@lipaflow/ui'
import { adminService } from '../services/admin'
import { usePageTitle } from '../lib/pageTitle'
import { apiErrorMessage } from '@lipaflow/shared'

const field = 'w-full rounded-button border border-hairline bg-surface p-2.5 text-body-sm text-ink focus:outline-none focus:ring-2 focus:ring-accent'
const humanise = (key: string) => key.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase())

/**
 * Sectors — the settings each sector (schools, property) gives every
 * customer in it: the words its screens use and which features are on.
 * (What a customer buys within a sector, such as a school's plan, is a
 * product; see Prices.)
 * Edited as fields and switches; only what changed is sent (a merge patch).
 */
export default function VerticalsPage() {
  usePageTitle('Sectors')
  const [selected, setSelected] = useState<VerticalInfo | null>(null)
  const { data: products, isLoading } = useQuery({ queryKey: ['verticals-admin'], queryFn: () => adminService.listVerticals() })

  return (
    <div className="space-y-6">
      <PageHeader
        title="Sectors"
        subtitle="What each sector calls things and which features it switches on for every customer in it."
      />
      {isLoading ? (
        <p role="status" className="text-body-sm text-ink-muted">Loading…</p>
      ) : !products?.length ? (
        <EmptyState title="No sectors found" description="Check that sector seed data exists in this environment." />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {products.map((p) => {
            const on = Object.values(p.features || {}).filter(Boolean).length
            const total = Object.keys(p.features || {}).length
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => setSelected(p)}
                className="text-left rounded-card border border-hairline bg-surface p-5 hover:border-accent/40 transition-colors duration-fast"
              >
                <div className="flex items-baseline justify-between gap-2">
                  <h2 className="font-outfit text-h4 text-ink">{p.name}</h2>
                  <span className="font-mono text-caption text-ink-faint">{p.code}</span>
                </div>
                <p className="text-body-sm text-ink-muted mt-1">{p.description || 'No description yet.'}</p>
                <p className="text-caption text-ink-muted mt-3">
                  Calls payers “{(p.labels as Record<string, string>)?.payer ?? '—'}” and accounts “{(p.labels as Record<string, string>)?.entity ?? '—'}” · {on} of {total} features on
                </p>
              </button>
            )
          })}
        </div>
      )}
      <ProductPanel product={selected} onClose={() => setSelected(null)} />
    </div>
  )
}

function ProductPanel({ product, onClose }: { product: VerticalInfo | null; onClose: () => void }) {
  const queryClient = useQueryClient()
  const [labels, setLabels] = useState<Record<string, string>>({})
  const [features, setFeatures] = useState<Record<string, boolean>>({})
  const [raw, setRaw] = useState('')
  const [loadedFor, setLoadedFor] = useState<string | null>(null)

  if (product && loadedFor !== product.id) {
    setLoadedFor(product.id)
    setLabels({ ...(product.labels as Record<string, string>) })
    setFeatures({ ...(product.features as Record<string, boolean>) })
    setRaw('')
  }

  const save = useMutation({
    mutationFn: (config: Record<string, unknown>) => adminService.patchVerticalConfig(product!.id, config),
    onSuccess: () => {
      toast.success('Sector settings saved')
      queryClient.invalidateQueries({ queryKey: ['verticals-admin'] })
      queryClient.invalidateQueries({ queryKey: ['verticals'] })
      close()
    },
    onError: (e: unknown) => toast.error(apiErrorMessage(e, "Couldn't save")),
  })
  const close = () => { setLoadedFor(null); onClose() }

  if (!product) return <Modal open={false} onClose={close}>{null}</Modal>

  const origLabels = (product.labels || {}) as Record<string, string>
  const origFeatures = (product.features || {}) as Record<string, boolean>
  const changedLabels = Object.fromEntries(Object.entries(labels).filter(([k, v]) => v !== origLabels[k]))
  const changedFeatures = Object.fromEntries(Object.entries(features).filter(([k, v]) => v !== origFeatures[k]))
  const changes = Object.keys(changedLabels).length + Object.keys(changedFeatures).length

  const submit = () => {
    const patch: Record<string, unknown> = {}
    if (Object.keys(changedLabels).length) patch.labels = changedLabels
    if (Object.keys(changedFeatures).length) patch.features = changedFeatures
    if (raw.trim()) {
      try {
        const extra = JSON.parse(raw) as unknown
        if (!extra || typeof extra !== 'object' || Array.isArray(extra)) throw new Error()
        Object.assign(patch, extra)
      } catch {
        toast.error('Advanced settings must be a JSON object')
        return
      }
    }
    save.mutate(patch)
  }

  return (
    <Modal open onClose={close} title={product.name} size="lg">
      <div className="space-y-5">
        <p className="rounded-card bg-warning-soft px-4 py-3 text-body-sm text-ink">
          Changes here apply to every customer using {product.name}.
        </p>

        <section className="space-y-2">
          <h3 className="font-outfit text-h4 text-ink">Words used on screen</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {Object.keys(labels).map((key) => (
              <label key={key} className="block">
                <span className="block text-caption text-ink-muted mb-1">{humanise(key)}</span>
                <input
                  value={labels[key] ?? ''}
                  onChange={(e) => setLabels((l) => ({ ...l, [key]: e.target.value }))}
                  className={clsx(field, labels[key] !== origLabels[key] && 'ring-1 ring-accent')}
                />
              </label>
            ))}
          </div>
        </section>

        <section className="space-y-2">
          <h3 className="font-outfit text-h4 text-ink">Features</h3>
          <ul className="divide-y divide-hairline border border-hairline rounded-card">
            {Object.keys(features).map((key) => (
              <li key={key} className="flex items-center justify-between gap-3 px-4 py-2.5">
                <span className="text-body-sm text-ink">{humanise(key)}</span>
                <span className="flex items-center gap-2">
                  {features[key] !== origFeatures[key] && <Badge variant="info" size="sm">Changed</Badge>}
                  <button
                    type="button"
                    role="switch"
                    aria-checked={!!features[key]}
                    aria-label={humanise(key)}
                    onClick={() => setFeatures((f) => ({ ...f, [key]: !f[key] }))}
                    className={clsx('relative inline-flex h-6 w-11 items-center rounded-full transition-colors', features[key] ? 'bg-accent' : 'bg-hairline')}
                  >
                    <span className={clsx('inline-block h-4 w-4 rounded-full bg-surface transition-transform', features[key] ? 'translate-x-6' : 'translate-x-1')} />
                  </button>
                </span>
              </li>
            ))}
          </ul>
        </section>

        <details className="rounded-card border border-hairline">
          <summary className="cursor-pointer px-4 py-3 text-body-sm font-medium text-ink">Advanced: other settings as JSON</summary>
          <div className="px-4 pb-4 space-y-2">
            <p className="text-caption text-ink-muted">Merged into the product's settings. Leave empty unless you know the key you need.</p>
            <textarea rows={6} value={raw} onChange={(e) => setRaw(e.target.value)} placeholder='{"period_type": "term"}' className={clsx(field, 'font-mono')} aria-label="Advanced settings JSON" />
          </div>
        </details>

        <div className="flex items-center justify-between gap-2">
          <span className="text-caption text-ink-muted">{changes ? `${changes} ${changes === 1 ? 'change' : 'changes'}` : 'No changes yet'}</span>
          <span className="flex gap-2">
            <Button variant="ghost" onClick={close}>Cancel</Button>
            <Button onClick={submit} disabled={!changes && !raw.trim()} isLoading={save.isPending}>Save for every customer</Button>
          </span>
        </div>
      </div>
    </Modal>
  )
}
